import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faHeadset,
  faRobot,
  faCircleDot,
  faComments,
  faUserGear,
  faBolt,
} from "@fortawesome/free-solid-svg-icons"

/**
 * Declared icon NAMES -> renderable components.
 *
 * SHARED BY EVERY DECLARED-ICON SURFACE - the thread-mode toggle AND the chat
 * sender row resolve names here. One registry, one fallback. A second map would
 * be a second source of truth for the same names.
 *
 * PUBLISHED INTERFACE: an extension declares one of these names, so removing or
 * renaming a key breaks it in the estate with no change on their side. Add
 * freely, remove never.
 *
 * NAMES, NOT URLS (Roy, PLAT-3418 decision 1). A URL would let a third-party
 * extension load remote content into a page holding customer conversations.
 *
 * CONTRACT: every value is a component taking `{ size, ...rest }`, `size` any CSS
 * length defaulting to `1em`. Colour comes from `currentColor`, which is what
 * lets one entry serve both surfaces with no per-surface override.
 *
 * Values are COMPONENTS, not FA definitions, by construction: an FA definition is
 * one FILLED path, and the Heroicon is `fill="none"` with a stroke.
 */

/**
 * Wraps an FA definition to the component contract.
 *
 * WIDTH AND HEIGHT MUST BE `1em`, NOT `size`. FontAwesome scales from font-size,
 * and a relative `size` set on both COMPOUNDS - `width: 1.44em` resolves against
 * the font-size `fontSize: 1.44em` just set on the same element, giving a 16px
 * glyph in a 23.04px box. `1em` means "the font-size we just set", correct for
 * every unit.
 */
const fromFontAwesome = (definition) => {
  const Icon = ({ size = "1em", style, ...rest }) => (
    <FontAwesomeIcon
      icon={definition}
      style={{
        fontSize: size, width: "1em", height: "1em", ...style,
      }}
      {...rest}
    />
  )
  Icon.displayName = `FaIcon(${definition?.iconName || "unknown"})`
  return Icon
}

/**
 * Heroicons outline `sparkles`, MIT. Supplied by Roy 2026-08-31 GMT+8.
 *
 * `stroke="currentColor"` MUST stay - it is what lets one entry serve both
 * surfaces with no colour override at either.
 *
 * Effective stroke is `1.5 * rendered / 24`, so a caller rendering this below
 * 16px gets a sub-pixel line. Size it at 16px or above.
 */
const SparklesOutline = ({ size = "1em", ...rest }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="1.5"
    stroke="currentColor"
    width={size}
    height={size}
    aria-hidden="true"
    focusable="false"
    {...rest}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"
    />
  </svg>
)
SparklesOutline.displayName = "SparklesOutline"

const DECLARED_ICONS = {
  headset: fromFontAwesome(faHeadset),
  robot: fromFontAwesome(faRobot),
  // ONE ENTRY, TWO SURFACES: changing this moves the toggle segment AND the
  // bubble sender icon together. Do not fork it to change only one.
  sparkles: SparklesOutline,
  comments: fromFontAwesome(faComments),
  "user-gear": fromFontAwesome(faUserGear),
  bolt: fromFontAwesome(faBolt),
}

/**
 * An unrecognised name MUST render something neutral: a collapsed slot looks
 * broken and a throw takes the row with it. A dot, not the sparkle - that would
 * imply AI for a mode that might be anything.
 */
export const FALLBACK_DECLARED_ICON = fromFontAwesome(faCircleDot)

/**
 * Total by construction - a declaration arrives over the wire, so every input
 * including a number or a URL returns a usable component.
 */
export function declaredIcon(name) {
  if (typeof name !== "string") {
    return FALLBACK_DECLARED_ICON
  }
  return DECLARED_ICONS[name.trim().toLowerCase()] || FALLBACK_DECLARED_ICON
}

/** The names an extension may declare. Exported for docs and for the tests. */
export function knownDeclaredIconNames() {
  return Object.keys(DECLARED_ICONS)
}
